import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Eye, Globe, Grid3x3, Image as ImageIcon, Link as LinkIcon, Loader2, Map as MapIcon, Pencil, Plus, SearchX, Star, Trash2,
  TriangleAlert,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { fmtDate, fmtInt } from '../constants';
import type { Level } from '../worlds.types';
import { withGameGroup } from '../sharedMap';
import { LevelModal } from '../components/LevelModal';
import { STATIC_BACKGROUNDS, staticBackgroundFor } from '../map/staticBackgrounds';
import { confirmAction } from '../components/ConfirmDialog';
import { ActionButton, Dash, EmptyRow, InfoBox, SearchField, StatusBadge } from '../components/ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import lm from '../levelsMarkers.module.scss';

// ============================================================
// Onglet Mondes (niveaux) : les mondes Minecraft portant une grille de pays
// ============================================================
// Un niveau qui a des chunks, des marqueurs, ou qu'une région ou un biome référence n'est pas
// supprimable : le bouton est désactivé avec la raison (le satellite refuse de toute façon, 409
// détaillé affiché tel quel). ?nouveau=1 (état « aucun niveau », accueil) ouvre la création.
// ============================================================

const COLS = 10;

/** Niveaux qui ont une image fixe du terrain (texte de l'encart). */
const STATIC_NAMES = Object.keys(STATIC_BACKGROUNDS);

function matches(l: Level, q: string): boolean {
  if (!q) return true;
  return [l.name, l.title, ...l.sourceNames].some((v) => v.toLowerCase().includes(q));
}

/** Ce qui empêche la suppression (chunks, marqueurs, régions, biomes), vide si rien. */
function usesOf(l: Level, regions: number, biomes: number): string[] {
  const out: string[] = [];
  if (l.chunkCount > 0) out.push(`${fmtInt(l.chunkCount)} chunk(s)`);
  if (l.markerCount > 0) out.push(`${fmtInt(l.markerCount)} marqueur(s)`);
  if (regions > 0) out.push(`${fmtInt(regions)} région(s)`);
  if (biomes > 0) out.push(`${fmtInt(biomes)} biome(s) réservé(s)`);
  return out;
}

function TilesBadge({ level }: { level: Level }) {
  const url = level.tileUrl;
  const bg = staticBackgroundFor(level);
  if (bg) {
    return (
      <StatusBadge tone="off" icon={ImageIcon}
        title={`Aucune URL de tuiles : la carte affiche une image fixe du terrain ${bg.origin} en attendant les tuiles.`}>
        image fixe
      </StatusBadge>
    );
  }
  if (!url) {
    return (
      <StatusBadge tone="off" icon={Grid3x3} title="Aucune URL de tuiles : la carte affiche un fond neutre (quadrillage).">
        fond neutre
      </StatusBadge>
    );
  }
  const mixed = url.startsWith('http://') && window.location.protocol === 'https:';
  if (mixed) {
    return (
      <StatusBadge tone="warn" icon={TriangleAlert}
        title={`Tuiles en http : le navigateur les bloque sur une page https (contenu mixte).\n${url}`}>
        tuiles http
      </StatusBadge>
    );
  }
  return <StatusBadge tone="ok" icon={LinkIcon} title={url}>tuiles</StatusBadge>;
}

export function LevelsTab({ readOnly }: { readOnly: boolean }) {
  const levels = useWorldsStore((s) => s.levels);
  const levelsVersion = useWorldsStore((s) => s.levelsVersion);
  const mainLevel = useWorldsStore((s) => s.state?.mainLevel ?? null);
  const current = useWorldsStore((s) => s.level);
  const geoRegions = useWorldsStore((s) => s.geoRegions);
  const biomes = useWorldsStore((s) => s.biomes);
  const putLevel = useWorldsStore((s) => s.putLevel);
  const dropLevel = useWorldsStore((s) => s.dropLevel);
  const poll = useWorldsStore((s) => s.poll);
  const setLevel = useWorldsStore((s) => s.setLevel);
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<Level | 'new' | null>(null);
  const [q, setQ] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);

  // ?nouveau=1 : création demandée depuis l'état « aucun niveau » ou l'accueil, puis paramètre retiré.
  const wantsNew = params.get('nouveau') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    if (!readOnly) setEditing('new');
    const next = new URLSearchParams(params);
    next.delete('nouveau');
    setParams(next, { replace: true });
  }, [wantsNew, readOnly, params, setParams]);

  const refs = useMemo(() => {
    const regions = new Map<string, number>();
    const reserved = new Map<string, number>();
    geoRegions.forEach((r) => { if (r.assignedLevel) regions.set(r.assignedLevel, (regions.get(r.assignedLevel) ?? 0) + 1); });
    biomes.forEach((b) => { if (b.level) reserved.set(b.level, (reserved.get(b.level) ?? 0) + 1); });
    return { regions, reserved };
  }, [geoRegions, biomes]);

  const search = q.trim().toLowerCase();
  const filtered = useMemo(() => levels.filter((l) => matches(l, search)), [levels, search]);

  const showOnMap = (l: Level) => {
    if (l.name !== current) void setLevel(l.name);
    setParams(withGameGroup({ tab: 'carte' }, params));
  };

  const remove = async (l: Level) => {
    const ok = await confirmAction({
      title: 'Supprimer le niveau',
      message: <p>Supprimer le niveau <strong>{l.title}</strong> (<code>{l.name}</code>) ?</p>,
      details: (
        <>
          Il disparaît du sélecteur de niveau et de la carte. Aucun chunk, marqueur, région ni biome ne l'utilise.
          {l.name === mainLevel && <><br />C'est le niveau principal : la carte s'ouvrira ensuite sur un autre niveau.</>}
        </>
      ),
    });
    if (!ok) return;
    setRemoving(l.name);
    const res = await api.deleteLevel(l.name);
    setRemoving(null);
    // 409 détaillé du satellite (chunks, marqueurs, régions, biomes) : affiché tel quel.
    if (res.error) { toast.error(res.error); return; }
    dropLevel(l.name);
    toast.success(`Niveau ${l.name} supprimé`);
    void poll();
  };

  const saved = (l: Level) => {
    putLevel(l);
    // Le premier niveau créé devient le niveau principal : le poll recale la carte dessus.
    void poll();
  };

  const newBtn = !readOnly && (
    <button type="button" className={styles.primaryBtn} onClick={() => setEditing('new')}>
      <Plus size={14} /> Nouveau niveau
    </button>
  );

  return (
    <div className={ws.tabBody}>
      <InfoBox icon={Globe}>
        Les <strong>mondes Minecraft</strong> qui portent une grille de pays, des marqueurs et des régions. Le nom
        technique est celui du monde côté serveur (<code className={ws.codeChip}>overworld</code>) ; les noms
        d'origine font le lien avec NG Java (<code className={ws.codeChip}>world</code>,{' '}
        <code className={ws.codeChip}>DIM-28</code>). Sans URL de tuiles, la carte affiche une image fixe du terrain NG
        Java en attendant les tuiles quand il en existe une (
        {STATIC_NAMES.map((n, i) => <Fragment key={n}>{i > 0 && ', '}<code className={ws.codeChip}>{n}</code></Fragment>)}
        ), sinon un fond neutre.
      </InfoBox>

      <div className={ws.toolbar}>
        <div className={lm.toolbarMain}>
          {levels.length > 0 && (
            <SearchField value={q} onChange={setQ} placeholder="Filtrer par titre, nom ou nom d'origine"
              className={lm.search} />
          )}
          <span className={ws.counter} title="Niveaux affichés / niveaux déclarés">
            <strong>{fmtInt(filtered.length)}</strong> / {fmtInt(levels.length)} niveau(x)
          </span>
        </div>
        {newBtn}
      </div>

      <div className={`${ws.tableWrap} ${ws.tableScroll}`}>
        <table className={ws.table}>
          <thead>
            <tr>
              <th>Monde</th>
              <th>Nom technique</th>
              <th>Tuiles</th>
              <th className={ws.num}>Zoom max</th>
              <th className={ws.num}>Ordre</th>
              <th className={ws.num}>Chunks</th>
              <th className={ws.num}>Marqueurs</th>
              <th>Noms d'origine</th>
              <th>Modifié</th>
              <th className={`${ws.num} ${ws.stickyEnd}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const uses = usesOf(l, refs.regions.get(l.name) ?? 0, refs.reserved.get(l.name) ?? 0);
              return (
                <tr key={l.name}>
                  <td>
                    <span className={ws.nameCell}>
                      <span className={ws.levelIcon} aria-hidden><Globe size={16} /></span>
                      <span className={lm.titleCell}>
                        <span className={lm.titleText}>{l.title}</span>
                        {(l.name === mainLevel || l.name === current) && (
                          <span className={lm.badgeRow}>
                            {l.name === mainLevel && (
                              <StatusBadge tone="off" icon={Star}
                                title="Niveau ouvert par défaut : overworld s'il existe, sinon le premier par ordre">
                                principal
                              </StatusBadge>
                            )}
                            {l.name === current && (
                              <StatusBadge tone="info" icon={Eye} title="Niveau affiché sur la carte et dans les compteurs">
                                affiché
                              </StatusBadge>
                            )}
                          </span>
                        )}
                      </span>
                    </span>
                  </td>
                  <td><code className={ws.codeChip}>{l.name}</code></td>
                  <td><TilesBadge level={l} /></td>
                  <td className={ws.num}>{l.tileMaxZoom}</td>
                  <td className={ws.num}>{l.sortOrder}</td>
                  <td className={ws.num}>{l.chunkCount > 0 ? <span className={ws.strongNum}>{fmtInt(l.chunkCount)}</span> : <Dash />}</td>
                  <td className={ws.num}>{l.markerCount > 0 ? <span className={ws.strongNum}>{fmtInt(l.markerCount)}</span> : <Dash />}</td>
                  <td>
                    {l.sourceNames.length > 0 ? (
                      <span className={lm.badgeRow}>
                        {l.sourceNames.map((n) => <span key={n} className={`${ws.badge} ${ws.mono}`}>{n}</span>)}
                      </span>
                    ) : <Dash />}
                  </td>
                  <td className={`${styles.muted} ${ws.nowrap}`}>
                    {fmtDate(l.updatedAt)}{l.updatedBy ? ` · ${l.updatedBy}` : ''}
                  </td>
                  <td className={ws.stickyEnd}>
                    <span className={styles.rowActions}>
                      <ActionButton icon={MapIcon} tone="primary" title={`Voir ${l.title} sur la carte`} onClick={() => showOnMap(l)} />
                      {!readOnly && (
                        <>
                          <ActionButton icon={Pencil} tone="primary" title="Modifier" onClick={() => setEditing(l)} />
                          <ActionButton icon={Trash2} tone="danger" busy={removing === l.name}
                            disabled={uses.length > 0}
                            title={uses.length > 0 ? `Utilisé : ${uses.join(', ')}. Suppression impossible.` : 'Supprimer'}
                            onClick={() => void remove(l)} />
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
            {levels.length === 0 && levelsVersion === -1 && (
              <EmptyRow colSpan={COLS} icon={Loader2} spin title="Chargement des niveaux…" />
            )}
            {levels.length === 0 && levelsVersion !== -1 && (
              <EmptyRow colSpan={COLS} icon={Globe} title="Aucun niveau"
                text={readOnly
                  ? 'Aucun monde Minecraft n\'est déclaré pour l\'instant.'
                  : 'Déclare le premier monde Minecraft (par exemple overworld) pour peindre les pays et placer les marqueurs.'}
                actions={newBtn || undefined} />
            )}
            {levels.length > 0 && filtered.length === 0 && (
              <EmptyRow colSpan={COLS} icon={SearchX} title="Aucun niveau ne correspond"
                text={<>Aucun titre, nom technique ou nom d'origine ne contient « {q.trim()} ».</>}
                actions={(
                  <button type="button" className={styles.secondaryBtn} onClick={() => setQ('')}>Effacer la recherche</button>
                )} />
            )}
          </tbody>
        </table>
      </div>
      {editing && (
        <LevelModal level={editing === 'new' ? null : editing} levels={levels}
          onClose={() => setEditing(null)} onSaved={saved} />
      )}
    </div>
  );
}
